import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';
import { parametrosModalidade } from '../modalidades';
import { posicaoZCamadaCem2x2 } from './centralizacaoCem2x2';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// ── Helpers ────────────────────────────────────────────────────────────────────

function disposeGroup(group) {
  group.traverse(obj => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
      else obj.material.dispose();
    }
  });
}

// ── Palete EUR simplificado ───────────────────────────────────────────────────
// Origem Y=0: topo do palete; altura definida pela modalidade.

function buildPalete(parametros) {
  const meioC = parametros.comprimentoMm / 2;
  const meioL = parametros.larguraMm / 2;
  const escalaAltura = parametros.alturaPaleteMm / 145;
  const group = new THREE.Group();

  const matLongarina = new THREE.MeshStandardMaterial({ color: 0xb08858, roughness: 0.85 });
  const matBloco     = new THREE.MeshStandardMaterial({ color: 0xa07848, roughness: 0.85 });
  const matRipa      = new THREE.MeshStandardMaterial({ color: 0xc89a6a, roughness: 0.8  });

  // 3 longarinas inferiores (100 × 22 × 1400), correm em Z, distribuídas em X
  const geoLong = new THREE.BoxGeometry(100, 22 * escalaAltura, parametros.larguraMm);
  [-meioC + 100, 0, meioC - 100].forEach(x => {
    const m = new THREE.Mesh(geoLong, matLongarina);
    m.position.set(x, -134 * escalaAltura, 0);
    m.receiveShadow = true;
    group.add(m);
  });

  // 9 cubos de apoio (100 × 78 × 100), grid 3×3, centro Y=-84
  const geoBloco = new THREE.BoxGeometry(100, 78 * escalaAltura, 100);
  [-meioC + 100, 0, meioC - 100].forEach(x => {
    [-meioL + 50, 0, meioL - 50].forEach(z => {
      const m = new THREE.Mesh(geoBloco, matBloco);
      m.position.set(x, -84 * escalaAltura, z);
      m.castShadow = true;
      m.receiveShadow = true;
      group.add(m);
    });
  });

  // 9 ripas superiores (2000 × 22 × 100), correm em X, distribuídas em Z
  const geoRipa = new THREE.BoxGeometry(parametros.comprimentoMm, 22 * escalaAltura, 100);
  const posicoesRipas = parametros.larguraMm === 1400
    ? [-650, -487, -325, -162, 0, 162, 325, 487, 650]
    : Array.from({ length: 9 }, (_, i) => (-meioL + 50) + i * (parametros.larguraMm - 100) / 8);
  posicoesRipas.forEach(z => {
    const m = new THREE.Mesh(geoRipa, matRipa);
    m.position.set(0, -11 * escalaAltura, z);
    m.receiveShadow = true;
    group.add(m);
  });

  return group;
}

// ── Caixa individual ──────────────────────────────────────────────────────────

function buildCaixa(xDim, yDim, zDim) {
  const group = new THREE.Group();

  const geo = new THREE.BoxGeometry(xDim, yDim, zDim);
  const mat = new THREE.MeshStandardMaterial({ color: 0xd4b3d8, roughness: 0.7, metalness: 0.0 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.castShadow = true;
  mesh.receiveShadow = true;

  const edgesGeo = new THREE.EdgesGeometry(geo);
  const edgesMat = new THREE.LineBasicMaterial({ color: 0x000000 });
  const edges = new THREE.LineSegments(edgesGeo, edgesMat);

  group.add(mesh);
  group.add(edges);
  return group;
}

// ── Camada de caixas ──────────────────────────────────────────────────────────

export function buildCamada(nAlinhadas, nGirada, orientacao, indiceCamada, c, l, a, espelhado = false, parametros, cem2x2 = false) {
  const PALLET_C_HALF = parametros.comprimentoMm / 2;
  const PALLET_L_HALF = parametros.larguraMm / 2;
  const group = new THREE.Group();
  const sinalX = espelhado ? -1 : 1;

  if (orientacao === 'longitudinal') {
    // comprimento → X, largura → Z (encostado na frente = +PALLET_L_HALF).
    // Camada mista (nGirada > 0): o bloco alinhado encosta na borda de trás em X
    // (em vez de centrado) para abrir a sobra de comprimento inteira e contígua
    // do outro lado, onde a(s) girada(s) entra(m) — ver Etapa 2/Passo 3B.
    // Camada espelhada (amarração, ver caller): nega só o X de cada centro — Z e
    // as dimensões passadas a buildCaixa não mudam, a frente continua rente.
    const xAlinhadas = cem2x2
      ? -(c + 2 * l) / 2 + c / 2
      : nGirada > 0 ? -PALLET_C_HALF + c / 2 : 0;
    const zInicio = PALLET_L_HALF - l / 2;
    for (let i = 0; i < nAlinhadas; i++) {
      const caixa = buildCaixa(c, a, l);
      caixa.position.set(sinalX * xAlinhadas, indiceCamada * a + a / 2, zInicio - i * l);
      group.add(caixa);
    }

    if (nGirada > 0) {
      // Girada 90°: mesma técnica do branch 'atravessada' (troca de dimensões em
      // buildCaixa, sem mesh.rotation) — l no eixo X (cabe na sobra), c no eixo Z
      // (mesma fórmula de centralização "encostado na frente" do atravessada,
      // por isso o transbordo de ~34mm cai inteiro atrás, não dos dois lados).
      const xGiradaInicio = xAlinhadas + c / 2;
      const zGirada = PALLET_L_HALF - c / 2;
      for (let i = 0; i < nGirada; i++) {
        const girada = buildCaixa(l, a, c);
        girada.position.set(sinalX * (xGiradaInicio + l / 2 + i * l), indiceCamada * a + a / 2, zGirada);
        group.add(girada);
      }
    }
  } else {
    // atravessada: largura → X (centrado), comprimento → Z (encostado na frente = +PALLET_L_HALF)
    const n = nAlinhadas;
    const xTotal  = n * l;
    const xInicio = -xTotal / 2 + l / 2;
    const zCenter = PALLET_L_HALF - c / 2;
    for (let i = 0; i < n; i++) {
      const caixa = buildCaixa(l, a, c);
      caixa.position.set(xInicio + i * l, indiceCamada * a + a / 2, zCenter);
      group.add(caixa);
    }
  }

  return group;
}

// ── Componente ────────────────────────────────────────────────────────────────

const Visualizacao3D = forwardRef(function Visualizacao3D({ plano, volume, campos, parametros = parametrosModalidade('embalagem'), modalidade = 'embalagem', centralizacaoConjuntoZ = 0 }, ref) {
  const containerRef   = useRef(null);
  const rendererRef    = useRef(null);
  const sceneRef       = useRef(null);
  const cameraRef      = useRef(null);
  const controlsRef    = useRef(null);
  const animationIdRef = useRef(null);
  const layersRef      = useRef([]);   // THREE.Group por camada

  // ── Monta uma vez ───────────────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const w = container.clientWidth  || 800;
    const h = container.clientHeight || 450;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xfafafa);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, w / h, 100, 10000);
    camera.position.set(3000, 2200, 3000);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;
    container.appendChild(renderer.domElement);

    // Luzes
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));

    const dir1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dir1.position.set(1500, 2500, 1500);
    dir1.castShadow = true;
    dir1.shadow.mapSize.width  = 2048;
    dir1.shadow.mapSize.height = 2048;
    scene.add(dir1);
    scene.add(dir1.target);

    const dir2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dir2.position.set(-1500, 1500, -1500);
    scene.add(dir2);

    // Palete (construído uma única vez)
    const paleteGroup = buildPalete(parametros);
    scene.add(paleteGroup);

    // OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping  = true;
    controls.minDistance    = 1500;
    controls.maxDistance    = 8000;
    controls.maxPolarAngle  = Math.PI * 0.49;
    controls.target.set(0, 0, 0);
    controls.update();
    controlsRef.current = controls;

    // Loop de render
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize via ResizeObserver (mesmo padrão do Canvas3D.jsx)
    const handleResize = () => {
      if (!containerRef.current) return;
      const w2 = containerRef.current.clientWidth;
      const h2 = containerRef.current.clientHeight;
      camera.aspect = w2 / h2;
      camera.updateProjectionMatrix();
      renderer.setSize(w2, h2);
    };
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      controls.dispose();
      disposeGroup(paleteGroup);
      layersRef.current.forEach(lg => disposeGroup(lg));
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [parametros]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reage ao plano ──────────────────────────────────────────────────────────
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Remove camadas antigas e libera GPU
    layersRef.current.forEach(lg => {
      disposeGroup(lg);
      scene.remove(lg);
    });
    layersRef.current = [];

    const nAlinhadas     = campos.caixasAlinhadas ?? campos.caixasPorCamada;
    const nGirada        = campos.caixasGiradas ?? 0;
    const numCamadas     = campos.numCamadas;
    const orientacao     = campos.orientacao;
    const sobreposicaoMm = campos.sobreposicaoMm ?? 50;
    const c = volume.comprimento_mm;
    const l = volume.largura_mm;
    const a = volume.altura_mm;

    for (let i = 0; i < numCamadas; i++) {
      const ehImpar = i % 2 === 1;
      const espelhado = nGirada > 0 && ehImpar;
      const cem2x2 = modalidade === 'lojas_cem' && orientacao === 'longitudinal'
        && nAlinhadas === 2 && nGirada === 2;
      const layerGroup = buildCamada(nAlinhadas, nGirada, orientacao, i, c, l, a, espelhado, parametros, cem2x2);
      if (nGirada > 0) {
        // Camada mista: espelho em X nas impares. Somente CEM 2+2
        // centraliza o conjunto em Z e desloca as camadas alternadas.
        layerGroup.position.z = cem2x2
          ? posicaoZCamadaCem2x2(centralizacaoConjuntoZ, sobreposicaoMm, i)
          : 0;
      } else {
        // Camada pura: comportamento atual — sobreposição Z nas ímpares, sem espelho.
        layerGroup.position.z = ehImpar ? -sobreposicaoMm : 0;
      }
      scene.add(layerGroup);
      layersRef.current.push(layerGroup);
    }

    // Reaponta o pivot para o centro da pilha
    if (controlsRef.current) {
      controlsRef.current.target.set(0, (numCamadas * a) / 2, 0);
      controlsRef.current.update();
    }
  }, [
    campos.caixasPorCamada,
    campos.caixasAlinhadas,
    campos.caixasGiradas,
    campos.numCamadas,
    campos.orientacao,
    campos.sobreposicaoMm,
    volume.comprimento_mm,
    volume.largura_mm,
    volume.altura_mm,
    parametros,
    modalidade,
    centralizacaoConjuntoZ,
  ]);

  // ── API para Fase 4 ─────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    capturarAngulo: async (angulo) => {
      const renderer = rendererRef.current;
      const scene    = sceneRef.current;
      const camera   = cameraRef.current;
      const controls = controlsRef.current;
      const layers   = layersRef.current;
      if (!renderer || !scene || !camera) return null;

      // Salva estado
      const savedPos        = camera.position.clone();
      const savedQuat       = camera.quaternion.clone();
      const savedTarget     = controls ? controls.target.clone() : new THREE.Vector3();
      const savedVisibility = layers.map(lg => lg.visible);

      const a          = volume.altura_mm;
      const numCamadas = layers.length;

      // Configura visibilidade e câmera — XZ padronizado em 2400mm para todas as vistas
      const DIST_XZ = 2400;
      if (angulo === 'palet') {
        layers.forEach(lg => { lg.visible = false; });
        camera.position.set(DIST_XZ, 1400, DIST_XZ);
        camera.lookAt(0, 0, 0);
      } else if (angulo === 'camada1') {
        layers.forEach((lg, i) => { lg.visible = i === 0; });
        camera.position.set(DIST_XZ, 1150, DIST_XZ);
        camera.lookAt(0, a / 2, 0);
      } else if (angulo === 'camada2') {
        layers.forEach((lg, i) => { lg.visible = i <= 1; });
        camera.position.set(DIST_XZ, 1250, DIST_XZ);
        camera.lookAt(0, a, 0);
      } else { // 'completo'
        layers.forEach(lg => { lg.visible = true; });
        camera.position.set(DIST_XZ, 1600, DIST_XZ);
        camera.lookAt(0, numCamadas * a / 2, 0);
      }

      renderer.render(scene, camera);
      const dataURL = renderer.domElement.toDataURL('image/png', 0.95);

      // Restaura
      layers.forEach((lg, i) => { lg.visible = savedVisibility[i]; });
      camera.position.copy(savedPos);
      camera.quaternion.copy(savedQuat);
      if (controls) {
        controls.target.copy(savedTarget);
        controls.update();
      }

      return dataURL;
    },
  }), [volume]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          right: 10,
          fontSize: '0.68rem',
          color: 'rgba(120,120,120,0.65)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        Arraste para girar • Scroll para zoom
      </div>
    </div>
  );
});

export default Visualizacao3D;
